import { useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import emailjs from '@emailjs/browser';
import ReCAPTCHA from "react-google-recaptcha";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "@/hooks/useTranslation";
import { Mail, Phone, MapPin, Send } from "lucide-react";

const contactSchema = z.object({
  name: z.string().min(2, "El nombre debe tener al menos 2 caracteres"),
  email: z.string().email("Por favor ingresa un email válido"),
  phone: z.string().min(10, "El teléfono debe tener al menos 10 dígitos"),
  company: z.string().optional(),
  message: z.string().min(10, "El mensaje debe tener al menos 10 caracteres"),
});

type ContactFormData = z.infer<typeof contactSchema>;

const ContactSection = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);
  const { toast } = useToast();
  const { t } = useTranslation();

  const form = useForm<ContactFormData>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      name: "",
      email: "",
      phone: "",
      company: "",
      message: "",
    },
  });

  const onSubmit = async (data: ContactFormData) => {
    if (!captchaToken) {
      toast({
        title: "Verificación requerida",
        description: "Por favor completa la verificación reCAPTCHA.",
        variant: "destructive",
      });
      return;
    }

    setIsSubmitting(true);
    
    try {
      // EmailJS configuration
      const serviceId = 'service_racuxju';
      const templateId = 'service_global';
      const publicKey = 'KXI3OOWTquBgBoOTC';
      
      // Prepare the email parameters
      const templateParams = {
        from_name: data.name,
        from_email: data.email,
        phone: data.phone,
        company: data.company || 'No especificada',
        message: data.message,
        to_name: 'ENAUSA', // Your company name
      };

      // Send email using EmailJS
      const result = await emailjs.send(
        serviceId,
        templateId,
        templateParams,
        publicKey
      );

      console.log('Email sent successfully:', result);
      
      toast({
        title: t('contact.success.title') || "Mensaje enviado",
        description: t('contact.success.description') || "Nos pondremos en contacto contigo pronto.",
      });
      
      form.reset();
      setCaptchaToken(null);
      recaptchaRef.current?.reset();
    } catch (error) {
      console.error('Error sending email:', error);
      toast({
        title: t('contact.error.title') || "Error",
        description: t('contact.error.description') || "Hubo un problema al enviar el mensaje. Intenta nuevamente.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contacto" className="py-24 bg-gradient-to-br from-enausa-light to-background">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-enausa-navy mb-6">
            {t('contact.title')}
          </h2>
          <p className="text-lg sm:text-xl text-enausa-gray max-w-3xl mx-auto leading-relaxed px-4">
            {t('contact.subtitle')}
          </p>
        </div>

        <div className="max-w-4xl mx-auto">
          {/* Contact Form */}
          <div className="bg-card rounded-xl shadow-medium border p-4 sm:p-6 md:p-8">
            <h3 className="text-xl sm:text-2xl font-bold text-enausa-navy mb-6 sm:mb-8">
              {t('contact.title')}
            </h3>

            <Form {...form}>
              <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-enausa-navy">{t('contact.form.name')} *</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder={t('contact.form.name')} 
                            {...field}
                            className="focus:ring-enausa-gold focus:border-enausa-gold"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="email"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-enausa-navy">{t('contact.form.email')} *</FormLabel>
                        <FormControl>
                          <Input 
                            type="email" 
                            placeholder={t('contact.form.email')} 
                            {...field}
                            className="focus:ring-enausa-gold focus:border-enausa-gold"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name="phone"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-enausa-navy">{t('contact.form.phone')} *</FormLabel>
                        <FormControl>
                          <Input 
                            type="tel" 
                            placeholder={t('contact.form.phone')} 
                            {...field}
                            className="focus:ring-enausa-gold focus:border-enausa-gold"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="company"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel className="text-enausa-navy">{t('contact.form.company')}</FormLabel>
                        <FormControl>
                          <Input 
                            placeholder={t('contact.form.company')} 
                            {...field}
                            className="focus:ring-enausa-gold focus:border-enausa-gold"
                          />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FormField
                  control={form.control}
                  name="message"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-enausa-navy">{t('contact.form.message')} *</FormLabel>
                      <FormControl>
                        <Textarea 
                          placeholder={t('contact.form.message')}
                          className="min-h-[120px] focus:ring-enausa-gold focus:border-enausa-gold"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                {/* reCAPTCHA */}
                <div className="flex justify-center">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey="6LcS3MkrAAAAAE6TpPH_dz_1XtPyLaKs1xR8qflF"
                    onChange={(token) => setCaptchaToken(token)}
                    onExpired={() => setCaptchaToken(null)}
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full bg-enausa-navy hover:bg-enausa-navy-dark text-enausa-text-on-navy"
                  disabled={isSubmitting || !captchaToken}
                >
                  {isSubmitting ? (
                    "Enviando..."
                  ) : (
                    <>
                      <Send className="mr-2 h-4 w-4" />
                      {t('contact.form.send')}
                    </>
                  )}
                </Button>
              </form>
            </Form>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;